/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { Theme } from '@mui/material';
import {
  type APIPermissions,
  type AppURL,
  approvalLabelMap,
  type BidMatrixScenario,
  BidRequestInputApprovalStatus,
  type BidRequestInputTeamApproval,
  type BidRequestNotificationsPayload,
  type BidRequestProject,
  type BidRequestProjectTeammember,
  type BidRequestRecord,
  type BidRequestState,
  type BidRequestsQueryListColumns,
  calculateWeightedAverage,
  type IcrSpecificData,
  type IcrsPerProject,
  isNotificationActor,
  listToString,
  objectEntries,
  objectKeys,
  type ProjectLandSpecificData,
  type RemoveNulls,
  ScenarioAPIPermissions,
  type SelectedSites,
  type SiteListRecord,
  type SiteRecordType,
  type TeamsPerProject,
} from '@poweralpha/common';
import { find, get, meanBy, min, minBy, round, sortBy, sum, sumBy, uniq } from 'lodash';
import type { AuthenticatedUser } from '../auth';
import { getProcurementInputsForProjectCod, isProcurementInputComplete } from './data';
import type { EditBidRequestPayload } from './models';
import type { BidRequestScenarioForm } from './schemas';

const uniqueApprovals = (
  approvals: Array<BidRequestInputTeamApproval>,
  approval: BidRequestInputTeamApproval,
) => {
  if (!approvals.includes(approval)) {
    approvals.push(approval);
  }
};

export const hasSuperAdminApprovalPermission = (
  permissions: Array<APIPermissions> | undefined,
): boolean => Boolean(permissions?.includes(ScenarioAPIPermissions.WRITE_APPROVAL_SUPER_ADMIN));

export const hasDevInputEditPermission = (
  permissions: Array<APIPermissions> | undefined,
): boolean =>
  Boolean(
    permissions?.includes(ScenarioAPIPermissions.WRITE_APPROVAL_DEVELOPMENT) ||
      hasSuperAdminApprovalPermission(permissions),
  );

export const hasTxmInputEditPermission = (
  permissions: Array<APIPermissions> | undefined,
): boolean =>
  Boolean(
    permissions?.includes(ScenarioAPIPermissions.WRITE_APPROVAL_TXM) ||
      hasSuperAdminApprovalPermission(permissions),
  );

export const getBidRequestInputApprovals = (permissions: Array<APIPermissions>) => {
  const approvals: Array<BidRequestInputTeamApproval> = [];
  if (permissions.includes(ScenarioAPIPermissions.WRITE_APPROVAL_SUPER_ADMIN)) {
    uniqueApprovals(approvals, 'dev_approval');
    uniqueApprovals(approvals, 'txm_approval');
  }

  if (permissions.includes(ScenarioAPIPermissions.WRITE_APPROVAL_TXM)) {
    uniqueApprovals(approvals, 'txm_approval');
  }

  if (permissions.includes(ScenarioAPIPermissions.WRITE_APPROVAL_DEVELOPMENT)) {
    uniqueApprovals(approvals, 'dev_approval');
  }

  if (permissions.includes(ScenarioAPIPermissions.WRITE_APPROVAL_PROCUREMENT)) {
    uniqueApprovals(approvals, 'procurement_approval');
  }

  if (permissions.includes(ScenarioAPIPermissions.WRITE_APPROVAL_ENGINEERING)) {
    uniqueApprovals(approvals, 'eng_approval');
  }

  if (permissions.includes(ScenarioAPIPermissions.WRITE_APPROVAL_FINANCE)) {
    uniqueApprovals(approvals, 'finance_approval');
  }

  return approvals;
};

export const isNextDisabled = (
  activeStep: number,
  bidRequest: BidRequestRecord | undefined,
): { disabled: false; requiredText?: string } | { disabled: true; requiredText: string } => {
  if (activeStep >= 1) {
    if (bidRequest?.scenarios.filter((s) => s.pv_config_id ?? s.ess_config_id).length === 0) {
      return { disabled: true, requiredText: 'Engineering configs required' };
    }
  }

  if (activeStep >= 2) {
    if (bidRequest?.scenarios.filter((s) => s.pa_scenario_id).length === 0) {
      return { disabled: true, requiredText: 'Engineering inputs required' };
    }
    const unapprovedApprovals = objectKeys(approvalLabelMap)
      .map((key) => [key, bidRequest?.[key]])
      .filter(
        ([, approval]) => approval && approval !== BidRequestInputApprovalStatus.APPROVED,
      ) as Array<[BidRequestInputTeamApproval, BidRequestInputApprovalStatus]>;

    if (unapprovedApprovals.length) {
      return {
        disabled: true,
        requiredText: `${listToString(unapprovedApprovals.map(([key]) => approvalLabelMap[key]))} approval required`,
      };
    }
  }

  if (activeStep >= 3) {
    if (
      bidRequest?.scenarios.filter((s) => s.pricing_scenario_id).length !==
      (bidRequest?.scenarios.length ?? 0)
    ) {
      return { disabled: true, requiredText: 'Pricing required' };
    }
  }

  return { disabled: false };
};

const isDevApprovalReady = (bidRequest: BidRequestState) => {
  const numProjects = bidRequest.getProjectIds().length;
  return numProjects === bidRequest.projects.filter((p) => p?.land && p.dev).length;
};

const isTxmApprovalReady = (bidRequest: BidRequestState) => {
  const numProjects = bidRequest.getProjectIds().length;
  const haveAllIcrs = bidRequest.scenarios.every(
    (s) => s.bid_matrix_scenario.applicable_icrs.length > 0,
  );
  const haveAllTxmData = numProjects === bidRequest.projects.filter((p) => p?.txm).length;
  return haveAllIcrs && haveAllTxmData;
};

const isEngApprovalReady = (bidRequest: BidRequestState) =>
  (bidRequest.getEngInputsIds().length ?? 0) > 0 && Boolean(bidRequest.eng_inputs_filename);

const isProcurementApprovalReady = (bidRequest: BidRequestState) =>
  bidRequest.scenarios.every((scenario) =>
    isProcurementInputComplete(
      scenario.plant_type,
      getProcurementInputsForProjectCod(
        bidRequest.procurement_inputs,
        scenario.bid_matrix_scenario.project_id,
        scenario.bid_matrix_scenario.cod,
      ),
    ),
  );

const isFinanceApprovalReady = (bidRequest: BidRequestState) =>
  bidRequest.dev_approval === BidRequestInputApprovalStatus.APPROVED &&
  bidRequest.txm_approval === BidRequestInputApprovalStatus.APPROVED &&
  bidRequest.procurement_approval === BidRequestInputApprovalStatus.APPROVED &&
  bidRequest.eng_approval === BidRequestInputApprovalStatus.APPROVED;

const approvalRequirements: Record<
  BidRequestInputTeamApproval,
  { check: (bidRequest: BidRequestState) => boolean; text: string }
> = {
  dev_approval: {
    check: isDevApprovalReady,
    text: 'All land and dev inputs must be complete',
  },
  txm_approval: {
    check: isTxmApprovalReady,
    text: 'All TXM inputs must be complete',
  },
  eng_approval: {
    check: isEngApprovalReady,
    text: 'Engineering inputs required',
  },
  procurement_approval: {
    check: isProcurementApprovalReady,
    text: 'Procurement inputs required',
  },
  finance_approval: {
    check: isFinanceApprovalReady,
    text: 'All prior approvals required',
  },
};

export const isApproveDisabled = (
  bidRequest: BidRequestState | undefined | null,
  permissions: Array<APIPermissions> | undefined,
  selectedApprovals?: Array<BidRequestInputTeamApproval>,
): { disabled: false; requiredText?: string } | { disabled: true; requiredText: string } => {
  const approvals = selectedApprovals ?? getBidRequestInputApprovals(permissions ?? []);
  if (!bidRequest || approvals.length === 0) {
    return { disabled: false };
  }

  const requiredTexts = approvals.flatMap((approval) => {
    const requirement = approvalRequirements[approval];
    return requirement.check(bidRequest) ? [] : [requirement.text];
  });

  if (requiredTexts.length > 0) {
    return { disabled: true, requiredText: listToString(requiredTexts) };
  }

  return { disabled: false };
};

export const getApprovalsForReviewDialog = ({
  approval,
  bidRequestRecord,
  permissions,
}: {
  approval: BidRequestInputTeamApproval;
  bidRequestRecord: BidRequestState | undefined;
  permissions: Array<APIPermissions> | undefined;
}): Array<BidRequestInputTeamApproval> => {
  if (
    !bidRequestRecord ||
    !hasSuperAdminApprovalPermission(permissions) ||
    (approval !== 'dev_approval' && approval !== 'txm_approval')
  ) {
    return [approval];
  }

  const pendingApprovals = (
    ['dev_approval', 'txm_approval'] as const satisfies Array<BidRequestInputTeamApproval>
  ).filter(
    (candidateApproval) =>
      bidRequestRecord[candidateApproval] !== BidRequestInputApprovalStatus.APPROVED,
  );

  const areDevInputsReady = !isApproveDisabled(bidRequestRecord, permissions, ['dev_approval'])
    .disabled;
  const areTxmInputsReady = !isApproveDisabled(bidRequestRecord, permissions, ['txm_approval'])
    .disabled;

  if (
    pendingApprovals.length === 2 &&
    areDevInputsReady &&
    areTxmInputsReady &&
    pendingApprovals.includes(approval)
  ) {
    return [...pendingApprovals];
  }

  return pendingApprovals.includes(approval) ? [approval] : [...pendingApprovals];
};

export const summarizeIcrs = (
  icrs: Array<IcrSpecificData>,
): Partial<Omit<IcrSpecificData, 'lc_posting_date'>> & {
  lc_posting_date: Date | undefined;
} => ({
  ic_voltage_kv: icrs.at(0)?.ic_voltage_kv ?? 0,
  lc_posting_cost: sumBy(icrs, 'lc_posting_cost'),
  lc_posting_date: min(
    icrs
      .filter(({ lc_posting_date }) => lc_posting_date)
      .map(({ lc_posting_date }) => new Date(lc_posting_date ?? '')),
  ),
  nonreimb_nu_costs: sumBy(icrs, 'nonreimb_nu_costs'),
  reimb_nu_costs: sumBy(icrs, 'reimb_nu_costs'),
  poi_mw_ac: sumBy(icrs, 'poi_mw_ac'),
  refund_interest_rate: meanBy(icrs, 'refund_interest_rate'),
  refund_period_months: minBy(icrs, 'refund_period_months')?.refund_period_months,
});

export const compileNotifs = (
  teams: TeamsPerProject,
  opts: {
    notifAudience?: BidRequestNotificationsPayload['notifAudiencePerProject'];
    defaultNotifs?: {
      default: Array<BidRequestProjectTeammember> | undefined;
      user: AuthenticatedUser | undefined;
    };
  },
) =>
  Object.entries(teams).flatMap(([project_id, meta]) =>
    objectEntries(meta).map(([role, userMeta]) => {
      let notify = false;
      // An empty notifAudience map carries no signal (the precheck endpoint returns
      // `{}`), so fall through to the responsibleRoles-based defaults in that case.
      if (opts.notifAudience && Object.keys(opts.notifAudience).length > 0) {
        notify = opts.notifAudience[project_id]?.includes(role);
      } else if (opts.defaultNotifs) {
        notify =
          Boolean(userMeta?.name) &&
          Boolean(opts.defaultNotifs.default?.includes(role as BidRequestProjectTeammember)) &&
          !isNotificationActor(userMeta?.email, opts.defaultNotifs.user?.email);
      }

      return { project_id, role, notify, name: userMeta?.name ?? '' };
    }),
  );

export const guessApplicableIcrs = (
  scenario: Omit<BidMatrixScenario, 'id'>,
  projectId: string,
  icrsPerProject?: IcrsPerProject,
): Pick<BidRequestScenarioForm, 'applicable_icrs' | 'edited'> => {
  let applicable_icrs = [...scenario.applicable_icrs];

  // try to guess applicable icrs
  if (applicable_icrs?.length > 0 || !icrsPerProject) {
    return { applicable_icrs, edited: false };
  }

  const icrs = projectId in icrsPerProject ? icrsPerProject[projectId] : [];
  const possibleSubproject = find(icrs, (icr) => icr.ic_limit_mw_ac === scenario.poi_mw_ac);
  if (possibleSubproject?.id) {
    // icr matches poi exactly
    return { applicable_icrs: [possibleSubproject.id], edited: true };
  }

  const filteredIcrs = icrs.filter((s) => s.ic_limit_mw_ac);
  const poiMwAcSum = sum(icrs.map((icr) => icr.ic_limit_mw_ac));
  if (
    round(poiMwAcSum, 1) === round(scenario.poi_mw_ac, 1) &&
    uniq(icrs.map((icr) => icr.voltage_k_v)).length === 1
  ) {
    // sum of all POI limits equals desired POI
    applicable_icrs = filteredIcrs.map(({ id }) => id);
    return { applicable_icrs, edited: applicable_icrs.length > 0 };
  }

  let totalMwAc = 0;
  let voltage: number | null;
  const sortedIcrs = sortBy(filteredIcrs, 'expected_poi_capacity');
  sortedIcrs.reverse();
  sortedIcrs.forEach((icr) => {
    if (scenario.poi_mw_ac > totalMwAc && (voltage ? voltage === icr.voltage_k_v : true)) {
      applicable_icrs.push(icr.id);
      totalMwAc += icr.ic_limit_mw_ac ?? 0;
      voltage = icr.voltage_k_v;
    }
  });

  return { applicable_icrs, edited: applicable_icrs.length > 0 };
};

export const determineBidRequestLandingPage = (
  permissions: Array<APIPermissions> | undefined,
): AppURL => {
  if (!permissions) return 'bid-matrix';

  if (hasSuperAdminApprovalPermission(permissions)) {
    return 'projects';
  }

  if (hasDevInputEditPermission(permissions)) {
    return 'projects';
  }

  if (hasTxmInputEditPermission(permissions)) {
    return 'projects';
  }

  if (permissions.includes(ScenarioAPIPermissions.WRITE_APPROVAL_ENGINEERING)) {
    return 'eng-inputs';
  }

  if (permissions.includes(ScenarioAPIPermissions.WRITE_APPROVAL_PROCUREMENT)) {
    return 'procurement-inputs';
  }

  if (permissions.includes(ScenarioAPIPermissions.WRITE_APPROVAL_FINANCE)) {
    return 'scenarios';
  }

  return 'bid-matrix';
};

type SiteAverages<Type extends keyof SelectedSites> = RemoveNulls<
  Pick<
    ProjectLandSpecificData,
    Type extends 'lease'
      ? 'lease_escalation' | 'lease_cost'
      : Type extends 'owned_in_fee'
        ? 'owned_in_fee_cost'
        : 'purchase_cost'
  >
>;

export const calculateSiteAverages = <Type extends keyof SelectedSites>(
  sites: Array<SiteListRecord> | undefined,
  siteType: Type,
): SiteAverages<Type> => {
  if (siteType === 'lease') {
    const leaseSitesWithCosts = sites?.filter((s) => s.lease_cost_per_acre) ?? [];
    const leaseCostAvg = calculateWeightedAverage(
      leaseSitesWithCosts.map((s) => s.lease_cost_per_acre ?? 0),
      leaseSitesWithCosts.map((s) => s.size ?? 0),
    );
    const leaseEscalationAvg = calculateWeightedAverage(
      leaseSitesWithCosts.map((s) => s.lease_escalation ?? 0),
      leaseSitesWithCosts.map((s) => s.size ?? 0),
    );

    return { lease_cost: leaseCostAvg, lease_escalation: leaseEscalationAvg } as SiteAverages<Type>;
  }

  if (siteType === 'owned_in_fee') {
    const ownedInFeeSitesWithCosts = sites?.filter((s) => s.purchase_costs_per_acre) ?? [];
    const ownedInFeeCostAvg = calculateWeightedAverage(
      ownedInFeeSitesWithCosts.map((s) => s.purchase_costs_per_acre ?? 0),
      ownedInFeeSitesWithCosts.map((s) => s.size ?? 0),
    );

    return { owned_in_fee_cost: ownedInFeeCostAvg } as SiteAverages<Type>;
  }

  const purchaseSitesWithCosts = sites?.filter((s) => s.purchase_costs_per_acre) ?? [];
  const purchaseCostAvg = calculateWeightedAverage(
    purchaseSitesWithCosts.map((s) => s.purchase_costs_per_acre ?? 0),
    purchaseSitesWithCosts.map((s) => s.size ?? 0),
  );

  return { purchase_cost: purchaseCostAvg } as SiteAverages<Type>;
};

export const filterZeroAcreSites = (
  sites: Array<SiteListRecord> | undefined,
  type: SiteRecordType,
): Array<SiteListRecord> =>
  sites
    ?.filter((site) => (site.record_type_name === type ? site.size : false))
    .map((site) => ({
      ...site,
      landowner_name: site.landowner_name ?? site.landowner_account_name,
    })) ?? [];

export const areProjectInputsComplete = (
  projects: Array<BidRequestProject>,
  projectUpdate: EditBidRequestPayload['project_update'],
  projectId: string,
  permissions: Array<APIPermissions> | undefined,
) => {
  const projectData =
    projects.find((p) => p.project_id === projectId) ?? get(projectUpdate, projectId);

  if (hasSuperAdminApprovalPermission(permissions)) {
    return Boolean(projectData?.land && projectData?.dev && projectData?.txm);
  }

  if (hasDevInputEditPermission(permissions)) {
    return Boolean(projectData?.land && projectData?.dev);
  }

  if (hasTxmInputEditPermission(permissions)) {
    return Boolean(projectData?.txm);
  }

  return Boolean(projectData?.land && projectData?.txm && projectData?.dev);
};

export const getStatusColor = (br: BidRequestsQueryListColumns, theme: Theme) => {
  if (br?.completed) {
    return 'success.main';
  }
  if (br?.aborted) {
    return 'error.main';
  }
  return `${theme.palette.mode === 'dark' ? 'light' : 'dark'}gray`;
};
